import DirectApi from '../../directApi';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {LOAD_MORE, LOAD_MORE_DIRECTION} from './loading/IncrementalLoadingOverlay';
import DataManager from './DataManager';
import {DATE_FORMAT_DAY} from '../../constants';
import {
	getCanvasTimelineDateFromMoment,
	getMomentFromCanvasTimelineDate,
	useItemsLazyLoading,
} from './canvas-timeline/canvas_timeline_util';
import {SCALE_SETTINGS} from './canvas-timeline/canvas_timeline';
import {
	MIN_LOAD_MORE_MONTHS,
	MAX_LOAD_MORE_MONTHS,
	MONTHS_TO_LOAD_AS_BUFFER,
	setIsLoadingMoreData,
} from './loading/LoadMoreUtil';

let abortController = null;

const isAbortError = error => ('' + error).includes('abort');

// Matches the classification in directApi, so a genuine connectivity failure gets the
// network wording and a malformed payload does not.
const NETWORK_ERROR_MESSAGES = [
	'Failed to fetch',
	'Load failed',
	'NetworkError when attempting to fetch resource',
	'The Internet connection appears to be offline',
	'The network connection was lost',
];

const looksLikeNetworkError = error => NETWORK_ERROR_MESSAGES.some(message => ('' + error).includes(message));

const getTimestampCorrelationIdFromUrl = url => {
	try {
		const queryString = ('' + url).split('?')[1];
		return queryString ? new URLSearchParams(queryString).get('timestampCorrelationId') : null;
	} catch (e) {
		// A malformed URL must never mask the failure being described.
		return null;
	}
};

const getEndpointName = url => {
	const withoutQuery = ('' + url).split('?')[0];
	return withoutQuery.replace(/^https?:\/\/[^/]+\//, '') || withoutQuery;
};

const SERVER_ERROR_PREFIX = 'Internal Error: ';

/**
 * Pull the backend's correlation id out of an error body. This is the id the server
 * logged and the one the error reporting builds its log and trace links from, so it is
 * preferred over the client-side request id wherever the server got far enough to send
 * one. Matches how the mutation and query error paths read it.
 */
const getServerCorrelationId = serverMessage => {
	const message = serverMessage ? '' + serverMessage : '';
	const index = message.indexOf(SERVER_ERROR_PREFIX);
	if (index === -1) {
		return null;
	}
	return message.slice(index + SERVER_ERROR_PREFIX.length).trim() || null;
};

/**
 * Shape a failed load into the sentinel every caller checks for. The diagnostic fields
 * are carried on the object rather than reported from here, because the error modal the
 * caller opens is what reports to Sentry -- reporting here as well would file a second,
 * unmerged event for the same failure.
 */
const buildLoadError = (requestUrl, {status = 0, isNetworkError = false, serverMessage = null} = {}) => {
	const timestampCorrelationId = getTimestampCorrelationIdFromUrl(requestUrl);

	return {
		error: true,
		status,
		// A 401 means the session is gone rather than the request being broken, so callers
		// route it to the app's logged-out handling instead of a generic error modal.
		isNoViewer: status === 401,
		isNetworkError,
		endpoint: getEndpointName(requestUrl),
		// The server id where the request reached the server, since that is what the
		// error reporting resolves its links against; the client-side request id is the
		// fallback for a request that never got an answer.
		correlationId: getServerCorrelationId(serverMessage) || timestampCorrelationId,
		timestampCorrelationId,
		serverMessage,
	};
};

/**
 * True for both shapes a failed load can take: the error sentinel, and the `undefined`
 * an aborted request resolves to.
 */
export const isLoadFailure = data => !data || data.error === true;

/**
 * Open the standard error modal for a failed scheduling load. Passing `endpoint` and
 * `correlationId` through is what gives the Sentry event raised by the modal enough
 * context to trace the request that failed.
 */
export const showLoadFailureModal = (loadError, {reloadToUpcoming = true} = {}) => {
	showModal({
		type: MODAL_TYPE.MUTATION_ERROR,
		isNetworkError: !!(loadError && loadError.isNetworkError),
		errorType: loadError && loadError.isNoViewer ? 'No Viewer' : undefined,
		endpoint: loadError && loadError.endpoint,
		correlationId: loadError && loadError.correlationId,
		reload_to_upcoming: reloadToUpcoming,
	});
};

/**
 * Surface a throw raised while applying a loaded payload. The page sets its loaded flag
 * at the end of that work, so a throw part-way through leaves it on its loading state
 * forever with nothing shown -- the same symptom as a request that never lands. Attach
 * this to every chain that turns a scheduling payload into page state.
 *
 * The error is re-thrown so it still reaches the global handler with its stack intact;
 * this only guarantees the user is told rather than left waiting.
 */
export const showHandlerFailureModal = (endpoint, error) => {
	showLoadFailureModal({error: true, endpoint, serverMessage: '' + error});
	throw error;
};

/**
 * Fetch scheduling payloads, resolving each one to either the payload or the failure
 * sentinel that `isLoadFailure` recognises. Never rejects for a request that reached a
 * conclusion, so a caller cannot be left waiting on a promise that never settles.
 *
 * A request object takes `url` and `method`, optionally `body`, and optionally its own
 * `headers` and `credentials` — the shared project-scheduling view sends a share token
 * instead of cookies, so it needs both.
 */
export const fetchDataPromises = requestObjects => {
	const defaultHeaders = new Headers();
	defaultHeaders.append('Content-Type', 'application/json');
	if (window.AbortController) {
		//Not supported by IE
		abortController = new window.AbortController();
	}
	const signal = abortController ? abortController.signal : undefined;
	const promises = [];

	for (const requestObject of requestObjects) {
		const init = {
			headers: requestObject.headers || defaultHeaders,
			signal,
			credentials: 'credentials' in requestObject ? requestObject.credentials : 'include',
			method: requestObject.method,
			body: requestObject.body ? JSON.stringify(requestObject.body) : undefined,
		};
		promises.push(
			fetch(requestObject.url, init)
				.then(response => {
					if (response.ok) {
						return response.json();
					}

					// Read the body so the status and the backend's message reach the error
					// modal. A body that is missing or is not JSON must not mask the status.
					return response.text().then(
						text => {
							let serverMessage = null;
							try {
								serverMessage = text ? JSON.parse(text).message || null : null;
							} catch (e) {
								serverMessage = text || null;
							}
							return buildLoadError(requestObject.url, {status: response.status, serverMessage});
						},
						() => buildLoadError(requestObject.url, {status: response.status})
					);
				})
				.catch(error => {
					//Most likely aborted in unmount
					if (isAbortError(error)) {
						return undefined;
					}

					// Resolve to the same sentinel a non-OK response produces, so a rejected
					// load (dead request, empty body, JSON parse failure) takes the caller's
					// error path instead of rejecting Promise.all. Callers only guard against a
					// non-OK status, so a rejection here used to leave the page on its loading
					// spinner forever with nothing surfaced to the user.
					return buildLoadError(requestObject.url, {
						isNetworkError: looksLikeNetworkError(error),
						serverMessage: '' + error,
					});
				})
		);
	}

	return Promise.all(promises);
};

export const fetchInit = shareUrl => {
	const headers = new Headers();
	if (shareUrl) {
		headers.append('X-FORECAST-PROJECT-SCHEDULING-SHARE', shareUrl);
	}
	const init = {headers, method: 'GET'};
	if (!shareUrl) {
		init.credentials = 'include';
	}
	return DirectApi.Fetch_WithErrorHandling('scheduling/init', init).then(response => response.json());
};

const calculateMonthsToFetch = (pageComponent, stepsToFetch) => {
	const {timeline} = pageComponent;
	const {minorStep} = timeline;

	const isViewingInMonths = minorStep === SCALE_SETTINGS.MONTH.minorStep;
	const isViewingInQuarters = minorStep === SCALE_SETTINGS.QUARTER.minorStep;

	if (isViewingInMonths || isViewingInQuarters) {
		const quarterFactor = isViewingInQuarters ? 3 : 1;
		const monthsToFetch = stepsToFetch * quarterFactor + MONTHS_TO_LOAD_AS_BUFFER;
		return Math.min(MAX_LOAD_MORE_MONTHS, Math.max(MIN_LOAD_MORE_MONTHS, monthsToFetch));
	}

	return MIN_LOAD_MORE_MONTHS;
};

const getLoadMoreInit = (pageComponent, fetchMoreDate, direction, monthsToFetch) => {
	const {isProjectTimeline, schedulingView} = pageComponent.props;
	const {staffingModeActive} = pageComponent.state;

	const reqBody = {
		fetchMoreDate,
		direction,
		monthsToFetch,
		schedulingView,
		staffingModeActive,
	};

	if (isProjectTimeline) {
		const {projectId, groupId, programPrefix} = pageComponent.props;

		if (programPrefix && useItemsLazyLoading(pageComponent)) {
			const {data} = pageComponent.state;
			reqBody.programProjectIds = data.projects.map(project => project.rawId);
		}

		reqBody.excludedCompanyProjectId = projectId;
		reqBody.excludedCompanyProjectGroupId = groupId;
		reqBody.excludeIdleTimes = true;
	}

	const headers = new Headers();
	headers.append('Content-Type', 'application/json');

	return {
		headers,
		method: 'POST',
		credentials: 'include',
		body: JSON.stringify(reqBody),
	};
};

const getLoadMoreHistoryCacheKey = (canvasDate, direction) => {
	return `${canvasDate}-${direction}`;
};

const loadMoreDataInDirection = (pageComponent, direction, fromCanvasDate, stepsHidden) => {
	const {loadMoreHistory} = pageComponent.props;
	const historyKey = getLoadMoreHistoryCacheKey(fromCanvasDate, direction);

	if (!loadMoreHistory.has(historyKey)) {
		loadMoreHistory.add(historyKey);

		pageComponent.setState(
			{
				[LOAD_MORE.IS_LOADING + direction]: true,
			},
			() => {
				const fetchMoreMomentDate = getMomentFromCanvasTimelineDate(fromCanvasDate);
				const fetchMoreDate = fetchMoreMomentDate.format(DATE_FORMAT_DAY);

				const monthsToFetch = calculateMonthsToFetch(pageComponent, stepsHidden);
				const fetchMorePastInit = getLoadMoreInit(pageComponent, fetchMoreDate, direction, monthsToFetch);

				DirectApi.Fetch_WithErrorHandling('scheduling/heatmap/fetch_more/', fetchMorePastInit).then(async response => {
					if (!pageComponent.timeline) {
						// While waiting for fetch-more, user navigated away, so don't try to apply the response.
						return;
					}

					const mergeStartDate = getCanvasTimelineDateFromMoment(fetchMoreMomentDate);
					const newLoadMoreDate =
						direction === LOAD_MORE_DIRECTION.LEFT
							? fetchMoreMomentDate.subtract(monthsToFetch, 'months')
							: fetchMoreMomentDate.add(monthsToFetch, 'months');
					const mergeEndDate = getCanvasTimelineDateFromMoment(newLoadMoreDate);

					DataManager.mergeLoadMoreData(pageComponent, await response.json(), mergeStartDate, mergeEndDate);

					setIsLoadingMoreData(pageComponent, direction, false, getCanvasTimelineDateFromMoment(newLoadMoreDate));
				});
			}
		);
	}
};

export const fetchMoreData = (pageComponent, leftCanvasDate, rightCanvasDate, leftStepsHidden, rightStepsHidden) => {
	const isLoadingLeft = pageComponent.state[LOAD_MORE.IS_LOADING + LOAD_MORE_DIRECTION.LEFT];
	const isLoadingRight = pageComponent.state[LOAD_MORE.IS_LOADING + LOAD_MORE_DIRECTION.RIGHT];

	const hasLeftStepsHidden = leftStepsHidden > 0;
	const hasRightStepsHidden = rightStepsHidden > 0;

	if (!isLoadingLeft && hasLeftStepsHidden) {
		loadMoreDataInDirection(pageComponent, LOAD_MORE_DIRECTION.LEFT, leftCanvasDate, leftStepsHidden);
	}

	if (!isLoadingRight && hasRightStepsHidden) {
		loadMoreDataInDirection(pageComponent, LOAD_MORE_DIRECTION.RIGHT, rightCanvasDate, rightStepsHidden);
	}
};
